import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Sparkles,
  TrendingUp,
  Cpu,
  Cloud,
  Shield,
  Code,
  Briefcase,
  CheckCircle,
  ChevronRight,
} from "lucide-react";

const BlogPage = () => {
  const navigate = useNavigate();

  const trends = [
    {
      icon: Cpu,
      title: "Generative AI & Agentic Systems",
      points: [
        "LLMs are now integrated into customer support, coding (Copilot, Cursor) and analytics workflows.",
        "Demand surge for prompt engineers, AI/ML engineers, and product managers who can ship AI features.",
        "Companies hire faster for candidates who can build, fine-tune or deploy models in production.",
      ],
    },
    {
      icon: Cloud,
      title: "Cloud, DevOps & Platform Engineering",
      points: [
        "Multi-cloud (AWS + Azure + GCP) and Kubernetes skills remain the highest-paying mid-career stack.",
        "Hiring focus is shifting from generic DevOps to Platform / SRE engineers with cost-optimisation expertise.",
        "Infrastructure-as-Code (Terraform, Pulumi) and observability (Prometheus, Grafana, Datadog) are must-haves.",
      ],
    },
    {
      icon: Shield,
      title: "Cybersecurity & Cloud Security",
      points: [
        "Sharp rise in zero-trust architecture, SOC analyst and DevSecOps roles across BFSI and Telecom.",
        "Certifications like CEH, CompTIA Security+, AWS Security Specialty are weighted heavily in screening.",
        "Hands-on experience with SIEM, SOAR, EDR tools is now expected even for L1 candidates.",
      ],
    },
    {
      icon: Code,
      title: "Data Engineering & Modern Analytics",
      points: [
        "Companies are migrating from legacy ETL to modern data stacks — Snowflake, Databricks, dbt, Airflow.",
        "Strong SQL + at least one orchestration tool + Python is the new baseline for data roles.",
        "Real-time data (Kafka, Flink) and Lakehouse architectures are growing 2x year-on-year.",
      ],
    },
    {
      icon: TrendingUp,
      title: "Full-Stack & TypeScript-First Development",
      points: [
        "TypeScript, Next.js / Nest.js, and serverless architectures dominate new project starts in 2026.",
        "Recruiters expect candidates to have 1–2 deployed side-projects with live URLs and GitHub history.",
        "Mobile-first responsive UI and performance budgets are now standard interview topics.",
      ],
    },
    {
      icon: Briefcase,
      title: "GCC & Hybrid Workforce Boom",
      points: [
        "Global Capability Centres in Pune, Hyderabad, Bangalore and Gurugram are hiring across every IT function.",
        "Hybrid work (3 days office + 2 remote) is now the most common offer — fully-remote roles have shrunk.",
        "International voice & non-voice support, cloud admin, and tier-2 engineering are very active categories.",
      ],
    },
  ];

  const checklist = [
    {
      title: "Pick a depth-first, not breadth-first skill plan",
      desc: "Choose one stack (e.g., Java + Spring + AWS, or Python + Django + Postgres) and go deep with end-to-end projects. Recruiters reject 'jack of all trades' resumes.",
    },
    {
      title: "Build 1–2 portfolio projects with a live URL",
      desc: "Deploy on Vercel / Railway / AWS. Add GitHub, README, screenshots and a 1-line problem statement. This single thing 3x'es your callback rate.",
    },
    {
      title: "Get one industry-recognised certification",
      desc: "AWS Cloud Practitioner / Azure AI-900 / Google ACE / CompTIA Security+ — passed within the last 12 months. It de-risks the hiring decision for the manager.",
    },
    {
      title: "Practice mock interviews aloud",
      desc: "DSA + 1 system design round + 1 behavioural round per week. Pramp, ChatGPT, peer-pair — anything counts. Most rejections happen on communication, not knowledge.",
    },
    {
      title: "Tailor your resume per role",
      desc: "ATS systems scan for keywords. Mirror the JD's exact tools/versions in your skills line. Keep one resume per stack — Java, Cloud, Data, Frontend, etc.",
    },
    {
      title: "Maintain LinkedIn like a portfolio",
      desc: "Headline, About, recent posts, and skill endorsements. 70% of recruiters source from LinkedIn first. A thin profile = silent rejection.",
    },
    {
      title: "Be radically honest about notice period & expectations",
      desc: "Don't oversell joining timelines or CTC expectations. Mismatched expectations are the #1 reason offers get rescinded after BGV.",
    },
    {
      title: "Apply through the right partners",
      desc: "Walk-ins are slow. Apply through trusted staffing partners like MC Talent Solutions for direct access to client SPOCs and pre-screened interview slots.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/50 via-white to-cyan-50/30">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            data-testid="blog-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-orange-500" />
            <span className="text-xl font-bold text-gray-900">MC Talent Blog</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-5">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            <span className="text-orange-700 text-sm font-semibold tracking-wide uppercase">
              Career Insights · IT Sector
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="blog-title">
            Current Trends in the <span className="text-orange-500">IT Sector</span> &amp; How Applicants Should Stay Ready
          </h1>
          <div className="flex items-center gap-6 text-sm text-gray-500 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Published February 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>7 min read</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-orange-500" />
              <span>By MC Talent Solutions</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-8 md:p-10 prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg" data-testid="blog-intro">
                The Indian IT sector in 2026 looks very different from even two years ago. AI is reshaping
                every job description, hybrid is the new default, and clients now expect candidates to be
                "interview-ready in days, not weeks." For applicants, this is both an opportunity and a
                challenge — the ones who prepare deliberately are getting placed faster, at better packages,
                with shorter notice periods.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                At MC Talent Solutions, we work with hundreds of enterprise clients across IT/ITES, BFSI,
                Telecom, Manufacturing and the EV industry. Below is a candid look at what hiring managers
                are asking for in 2026 — and a step-by-step playbook for candidates to stand out.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trends */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-8">
            6 IT Hiring Trends Shaping <span className="text-orange-500">2026</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {trends.map((trend, idx) => {
              const Icon = trend.icon;
              return (
                <Card
                  key={idx}
                  className="border-0 shadow-md hover:shadow-xl transition-shadow bg-white"
                  data-testid={`blog-trend-${idx}`}
                >
                  <CardContent className="p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">{trend.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {trend.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-600 text-sm leading-relaxed">
                          <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-1" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full mb-4">
              <Sparkles className="h-4 w-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-semibold tracking-wide uppercase">
                Applicant Playbook
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold">
              How to Stay <span className="text-orange-400">Ready</span> for Your Next Role
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              The 8 things our recruiters consistently see in candidates who get shortlisted within 48 hours.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {checklist.map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-colors"
                data-testid={`blog-checklist-${idx}`}
              >
                <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing thoughts */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-8 md:p-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                The Bottom Line
              </h2>
              <p className="text-gray-700 leading-relaxed text-base mb-4">
                Skills decay quickly. The half-life of a tech skill in 2026 is roughly 18 months — what got
                you hired in 2024 might not even open the door today. Treat learning as a continuous practice,
                not a quarterly project. Spend 3–5 hours every week on skill upgrades, certifications and
                portfolio work, and you will always be 2x more attractive than the average candidate in the
                market.
              </p>
              <p className="text-gray-700 leading-relaxed text-base">
                And when you are ready, don&apos;t apply alone — partner with recruiters who actually know your
                target companies. That single change converts a "spray-and-pray" job hunt into a focused,
                short, successful one.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Ready to make your next move?
          </h2>
          <p className="text-orange-50 text-base md:text-lg mb-7 max-w-2xl mx-auto">
            Browse our latest IT openings or share your profile with our recruiters — we&apos;ll match
            you to roles you actually have a shot at.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate("/jobs")}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-7 py-6 text-base font-semibold"
              data-testid="blog-cta-jobs"
            >
              View Open Positions
              <ChevronRight className="ml-1 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate("/#contact")}
              variant="outline"
              className="border-white/40 text-white bg-white/10 hover:bg-white/20 rounded-full px-7 py-6 text-base font-semibold"
              data-testid="blog-cta-contact"
            >
              Talk to a Recruiter
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-gray-400 py-6 text-center text-sm">
        <p>© 2026 MC Talent Solutions Pvt. Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default BlogPage;
